import type { EntityFormErrorMap } from '@klear/ui/entity-form-state'
import type { SupplierForm } from '@klear/supplier/wire.client'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import type { Draft } from 'immer'
import isArray from 'lodash/isArray'
import values from 'lodash/values'
import noop from 'lodash/noop'
import { Check, Plus } from 'lucide-react'
import { CompanyLookup } from '@klear/organization-ui/company-lookup'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { Combobox } from '@klear/ui/components/combobox'
import { Label } from '@klear/ui/components/label'
import { useTraders } from '@klear/ui/hooks/use-traders'
import { useSupplierMutations } from '@klear/ui/hooks/use-suppliers'
import { useEntityWizardForm } from '@klear/ui/hooks/use-entity-wizard-form'
import { entityFormErrorEntries, hasEntityFormErrors } from '@klear/ui/entity-form-state'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { bindWizardSteps, type WizardStepModule } from '@klear/ui/wizard/flow'
import { WizardFlowContent } from '@klear/ui/wizard/flow-content'
import { useReportedWizardFlow } from '@klear/ui/wizard/use-step-group-nav'
import type { SupplierFormDirty } from '@klear/supplier/wire.client'
import {
  EMPTY_SUPPLIER_FORM,
  formValuesToCreateData,
  formValuesToUpdateData,
  supplierToFormValues,
  type SupplierRecord
} from '@klear/supplier-ui/supplier-commit-form'
import {
  clearSupplierFormErrors,
  markSupplierFormDirtyPaths,
  setSupplierFormField,
  type SupplierFormFieldPath,
  type SupplierFormFieldValue
} from '@klear/supplier-ui/supplier-form-state'
import {
  SupplierIdentifierFields,
  SupplierLocationFields,
  SupplierOrganizationFields
} from '@klear/supplier-ui/supplier-field-groups'

export type SupplierStepGroupResult = { supplierId: number; name: string }

export type SupplierFlowMode = 'create' | 'edit' | 'view'

export type SupplierFlowOptions = {
  traderId?: number
  showTraderSelect?: boolean
  showIdentifiers?: boolean
  showParentOrganization?: boolean
  showCompanyLookup?: boolean
}

const STEP = {
  TRADER: 'supplier-trader',
  LOOKUP: 'supplier-lookup',
  ORGANIZATION: 'supplier-organization',
  LOCATION: 'supplier-location',
  IDENTIFIERS: 'supplier-identifiers',
  REVIEW: 'supplier-review'
} as const

type StepId = (typeof STEP)[keyof typeof STEP]

type SupplierFieldGroupProps = {
  values: SupplierForm
  errors: EntityFormErrorMap
  onFieldChange: (path: SupplierFormFieldPath, value: SupplierFormFieldValue) => void
  onFormUpdate: (
    recipe: (draft: Draft<SupplierForm>) => void | SupplierFormFieldPath | readonly SupplierFormFieldPath[] | undefined,
    dirtyPaths?: SupplierFormFieldPath | readonly SupplierFormFieldPath[]
  ) => void
}

type SupplierStepCtx = {
  mode: SupplierFlowMode
  isEditing: boolean
  vals: SupplierForm
  errors: EntityFormErrorMap
  showTraderSelect: boolean
  showIdentifiers: boolean
  showParentOrganization: boolean
  showCompanyLookup: boolean
  traderOptions: Array<{ value: number; label: string }>
  traderLabel?: string
  viewExtras?: React.ReactNode
  fieldGroupProps: SupplierFieldGroupProps
  handleFieldChange: (path: SupplierFormFieldPath, value: SupplierFormFieldValue) => void
  validateBeforeNext: (stepId: StepId) => Promise<boolean>
  advance: () => void
}

function stepFields(stepId: StepId): SupplierFormFieldPath[] {
  switch (stepId) {
    case STEP.TRADER:
      return ['traderId']
    case STEP.ORGANIZATION:
      return ['name', 'description', 'parentOrganization', 'size']
    case STEP.LOCATION:
      return ['address.country', 'address.line1', 'address.city', 'address.state', 'address.postalCode', 'website']
    case STEP.IDENTIFIERS:
      return ['dunsNumber', 'lei', 'ein', 'vatNumber']
    default:
      return []
  }
}

function validateStepValues(stepId: StepId, vals: SupplierForm, showTraderSelect: boolean): EntityFormErrorMap {
  const errors: EntityFormErrorMap = {}
  if (stepId === STEP.TRADER && showTraderSelect && (vals.traderId == null || vals.traderId < 1)) {
    errors.traderId = 'Select a trader'
  }
  if (stepId === STEP.ORGANIZATION && !vals.name.trim()) {
    errors.name = 'Supplier name is required'
  }
  return errors
}

function ReviewLine({ label, value, error }: { label: string; value: string; error?: string }) {
  return (
    <div>
      <span className="font-medium text-muted-foreground">{label}</span>
      <p>{value || '—'}</p>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </div>
  )
}

function firstError(errors: EntityFormErrorMap, paths: readonly string[]): string | undefined {
  for (const path of paths) {
    const message = errors[path]
    if (message) return message
  }
  return undefined
}

function reviewShownPaths(options: {
  showTraderSelect: boolean
  showIdentifiers: boolean
  showParentOrganization: boolean
}): string[] {
  const paths = [
    'name',
    'description',
    'address.country',
    'address.line1',
    'address.line2',
    'address.city',
    'address.state',
    'address.postalCode',
    'website'
  ]
  if (options.showTraderSelect) paths.push('traderId')
  if (options.showParentOrganization) paths.push('parentOrganization')
  if (options.showIdentifiers) paths.push('dunsNumber', 'lei', 'ein', 'vatNumber')
  return paths
}

function ReviewBody({
  values: v,
  errors,
  showTraderSelect,
  showIdentifiers,
  showParentOrganization,
  traderLabel
}: {
  values: SupplierForm
  errors: EntityFormErrorMap
  showTraderSelect: boolean
  showIdentifiers: boolean
  showParentOrganization: boolean
  traderLabel?: string
}) {
  const addr = v.address
  const addressLine = [addr?.line1, addr?.city, addr?.state, addr?.postalCode].filter(Boolean).join(', ')
  const shown = new Set(reviewShownPaths({ showTraderSelect, showIdentifiers, showParentOrganization }))
  const leftover = entityFormErrorEntries(errors).filter(({ path }) => path !== 'root' && !shown.has(path))
  return (
    <Card>
      <CardHeader>
        <CardTitle>Review</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {showTraderSelect && <ReviewLine label="Trader" value={traderLabel ?? ''} error={errors.traderId} />}
        <ReviewLine label="Name" value={v.name} error={errors.name} />
        <ReviewLine label="Description" value={v.description} error={errors.description} />
        {showParentOrganization && (
          <ReviewLine label="Parent organization" value={v.parentOrganization} error={errors.parentOrganization} />
        )}
        <ReviewLine label="Country" value={v.address?.country ?? ''} error={errors['address.country']} />
        <ReviewLine
          label="Address"
          value={addressLine}
          error={firstError(errors, [
            'address.line1',
            'address.line2',
            'address.city',
            'address.state',
            'address.postalCode'
          ])}
        />
        <ReviewLine label="Website" value={v.website} error={errors.website} />
        {showIdentifiers && (
          <div className="grid grid-cols-2 gap-3 border-t pt-3">
            <ReviewLine label="DUNS" value={v.dunsNumber} error={errors.dunsNumber} />
            <ReviewLine label="LEI" value={v.lei} error={errors.lei} />
            <ReviewLine label="EIN" value={v.ein} error={errors.ein} />
            <ReviewLine label="VAT" value={v.vatNumber} error={errors.vatNumber} />
          </div>
        )}
        {leftover.map(({ path, message }) => (
          <p key={path} className="text-sm text-destructive">
            {message}
          </p>
        ))}
      </CardContent>
    </Card>
  )
}

function SupplierTraderStep({ ctx }: { ctx: SupplierStepCtx }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Trader</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <Label htmlFor="supplier-flow-trader">Trader</Label>
        <Combobox<number>
          value={ctx.vals.traderId ?? ''}
          valueType="number"
          options={ctx.traderOptions}
          disabled={ctx.mode !== 'create'}
          placeholder="Search traders..."
          onChange={(value) => ctx.handleFieldChange('traderId', value === '' ? undefined : value)}
          error={ctx.errors.traderId ? { message: ctx.errors.traderId } : undefined}
        />
        {ctx.errors.traderId ? <p className="text-sm text-destructive">{ctx.errors.traderId}</p> : null}
      </CardContent>
    </Card>
  )
}

function SupplierLookupStep({ ctx }: { ctx: SupplierStepCtx }) {
  return (
    <CompanyLookup
      values={ctx.vals}
      onFormUpdate={(recipe) =>
        ctx.fieldGroupProps.onFormUpdate((draft) => {
          const dirty = recipe(draft)
          if (dirty == null) return dirty
          const paths = isArray(dirty) ? dirty : [dirty]
          return paths.filter((path): path is SupplierFormFieldPath => path !== 'allianzEhid')
        })
      }
      required={false}
      includeAllianzEhid={false}
      overwriteEntityLabel="supplier"
      confirmOverwrites={ctx.mode === 'edit'}
      onSkip={ctx.advance}
      onApplied={ctx.advance}
    />
  )
}

function SupplierOrganizationStep({ ctx }: { ctx: SupplierStepCtx }) {
  return (
    <SupplierOrganizationFields
      {...ctx.fieldGroupProps}
      isEditing={ctx.isEditing}
      showParentOrganization={ctx.showParentOrganization}
    />
  )
}

function SupplierLocationStep({ ctx }: { ctx: SupplierStepCtx }) {
  return <SupplierLocationFields {...ctx.fieldGroupProps} isEditing={ctx.isEditing} />
}

function SupplierIdentifiersStep({ ctx }: { ctx: SupplierStepCtx }) {
  return <SupplierIdentifierFields {...ctx.fieldGroupProps} isEditing={ctx.isEditing} />
}

function SupplierReviewStep({ ctx }: { ctx: SupplierStepCtx }) {
  return (
    <>
      <ReviewBody
        values={ctx.vals}
        errors={ctx.errors}
        showTraderSelect={ctx.showTraderSelect}
        showIdentifiers={ctx.showIdentifiers}
        showParentOrganization={ctx.showParentOrganization}
        traderLabel={ctx.traderLabel}
      />
      {!ctx.isEditing && ctx.viewExtras ? <div className="mt-6">{ctx.viewExtras}</div> : null}
    </>
  )
}

const SUPPLIER_STEP_MODULES: Array<WizardStepModule<SupplierStepCtx>> = [
  {
    id: STEP.TRADER,
    label: 'Trader',
    visible: (ctx) => ctx.showTraderSelect,
    beforeNext: (ctx) => ctx.validateBeforeNext(STEP.TRADER),
    component: SupplierTraderStep
  },
  {
    id: STEP.LOOKUP,
    label: 'Lookup',
    visible: (ctx) => ctx.showCompanyLookup,
    component: SupplierLookupStep
  },
  {
    id: STEP.ORGANIZATION,
    label: 'Organization',
    beforeNext: (ctx) => ctx.validateBeforeNext(STEP.ORGANIZATION),
    component: SupplierOrganizationStep
  },
  {
    id: STEP.LOCATION,
    label: 'Address',
    beforeNext: (ctx) => ctx.validateBeforeNext(STEP.LOCATION),
    component: SupplierLocationStep
  },
  {
    id: STEP.IDENTIFIERS,
    label: 'Identifiers',
    visible: (ctx) => ctx.showIdentifiers,
    beforeNext: (ctx) => ctx.validateBeforeNext(STEP.IDENTIFIERS),
    component: SupplierIdentifiersStep
  },
  {
    id: STEP.REVIEW,
    label: 'Review',
    component: SupplierReviewStep
  }
]

export type SupplierFlowProps = SupplierFlowOptions & {
  mode: SupplierFlowMode
  supplier?: SupplierRecord
  supplierId?: number
  viewExtras?: React.ReactNode
  cancelOnFirstStep?: boolean
  onNavStateChange: (nav: WizardStepGroupNavState) => void
  onDirtyChange?: (dirty: boolean) => void
  onComplete: (result: SupplierStepGroupResult) => void
  onCancel: () => void
}

export function SupplierFlow({
  mode,
  supplier,
  supplierId,
  traderId,
  showTraderSelect = false,
  showIdentifiers = false,
  showParentOrganization = false,
  showCompanyLookup = true,
  viewExtras,
  cancelOnFirstStep = false,
  onNavStateChange,
  onDirtyChange,
  onComplete,
  onCancel
}: SupplierFlowProps) {
  const { traders } = useTraders()
  const isEditing = mode !== 'view'
  const includeCompanyLookup = (mode === 'create' || mode === 'edit') && showCompanyLookup
  const advanceRef = useRef<() => void>(noop)
  const advance = useCallback(() => {
    advanceRef.current()
  }, [])

  const defaultValues = useMemo<SupplierForm>(() => {
    const resolvedTraderId = supplier?.traderId ?? traderId
    const traderName = resolvedTraderId != null ? (traders?.find((t) => t.id === resolvedTraderId)?.name ?? '') : ''
    if (supplier) {
      return { ...supplierToFormValues(supplier), traderId: supplier.traderId, traderName }
    }
    return { ...EMPTY_SUPPLIER_FORM, traderId, traderName }
  }, [supplier, traderId, traders])

  const {
    previewNewSupplier,
    previewSupplier,
    commitNewSupplier,
    commitSupplier,
    isPreviewing,
    isCreating,
    isUpdating
  } = useSupplierMutations()
  const isSaving = isPreviewing || isCreating || isUpdating

  const resolveTraderId = useCallback(
    (vals: SupplierForm) => (showTraderSelect ? vals.traderId : (traderId ?? supplier?.traderId ?? vals.traderId)),
    [showTraderSelect, traderId, supplier?.traderId]
  )

  const form = useEntityWizardForm<
    SupplierForm,
    SupplierFormDirty,
    SupplierFormFieldPath,
    SupplierFormFieldValue,
    StepId
  >({
    defaultValues,
    setField: setSupplierFormField,
    markDirtyPaths: markSupplierFormDirtyPaths,
    clearErrors: clearSupplierFormErrors,
    stepFields,
    validateStep: (stepId, stepVals) => validateStepValues(stepId, stepVals, showTraderSelect),
    saveErrorFallback: 'Failed to save supplier. Please try again.'
  })
  const { values: vals, errors, updateValues, handleFieldChange, validateBeforeNext } = form
  const isDirty = isEditing && values(form.dirtyFields).some((v) => v === true)
  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const traderOptions = useMemo(() => traders?.map((t) => ({ value: t.id, label: t.name })) ?? [], [traders])

  const handleSubmit = useCallback(async () => {
    const selectedTraderId = resolveTraderId(vals)
    const validationErrors: EntityFormErrorMap = {}
    if (selectedTraderId == null || selectedTraderId < 1) {
      validationErrors[showTraderSelect ? 'traderId' : 'root'] = showTraderSelect
        ? 'Select a trader'
        : 'Missing trader context'
    }
    if (!vals.name.trim()) validationErrors.name = 'Supplier name is required'
    if (mode === 'edit' && supplierId == null) validationErrors.root = 'Missing supplier context'
    if (hasEntityFormErrors(validationErrors)) {
      form.setErrors(validationErrors)
      return
    }
    await form.runSubmit(async () => {
      if (mode === 'edit' && supplierId != null) {
        const data = formValuesToUpdateData(vals, form.dirtyFields)
        await previewSupplier({
          id: supplierId,
          traderId: selectedTraderId!,
          data
        })
        const result = await commitSupplier({
          id: supplierId,
          traderId: selectedTraderId!,
          data
        })
        if (result?.id == null) throw new Error('Saving the supplier did not return an id.')
        form.clearDirty()
        onComplete({ supplierId: result.id, name: result.name })
        return
      }

      const data = formValuesToCreateData(vals)
      await previewNewSupplier({
        traderId: selectedTraderId!,
        data
      })
      const result = await commitNewSupplier({ traderId: selectedTraderId!, data })
      if (result?.id == null) throw new Error('Saving the supplier did not return an id.')
      form.clearDirty()
      onComplete({ supplierId: result.id, name: result.name })
    })
  }, [
    showTraderSelect,
    vals,
    resolveTraderId,
    mode,
    supplierId,
    form,
    previewSupplier,
    previewNewSupplier,
    commitSupplier,
    commitNewSupplier,
    onComplete
  ])

  const fieldGroupProps = useMemo<SupplierFieldGroupProps>(
    () => ({
      values: vals,
      errors,
      onFieldChange: handleFieldChange,
      onFormUpdate: updateValues
    }),
    [vals, errors, handleFieldChange, updateValues]
  )

  const traderLabel = vals.traderId != null ? traderOptions.find((o) => o.value === vals.traderId)?.label : undefined

  const stepCtx = useMemo<SupplierStepCtx>(
    () => ({
      mode,
      isEditing,
      vals,
      errors,
      showTraderSelect,
      showIdentifiers,
      showParentOrganization,
      showCompanyLookup: includeCompanyLookup,
      traderOptions,
      traderLabel,
      viewExtras,
      fieldGroupProps,
      handleFieldChange,
      validateBeforeNext,
      advance
    }),
    [
      mode,
      isEditing,
      vals,
      errors,
      showTraderSelect,
      showIdentifiers,
      showParentOrganization,
      includeCompanyLookup,
      traderOptions,
      traderLabel,
      viewExtras,
      fieldGroupProps,
      handleFieldChange,
      validateBeforeNext,
      advance
    ]
  )

  const stepDefs = useMemo(() => bindWizardSteps(SUPPLIER_STEP_MODULES, stepCtx), [stepCtx])
  const firstVisibleId = stepDefs.find((s) => s.visible !== false)?.id ?? STEP.ORGANIZATION

  const flow = useReportedWizardFlow(stepDefs, {
    initialStepId: mode === 'view' ? STEP.REVIEW : firstVisibleId,
    onExitFirstStep: cancelOnFirstStep ? onCancel : undefined,
    stepSelection: 'free',
    onNavStateChange
  })
  advanceRef.current = () => {
    void flow.goNext()
  }

  const idleSubmitLabel = mode === 'edit' ? 'Confirm & save' : 'Create supplier'
  const busyLabel = isPreviewing ? 'Validating…' : 'Saving…'

  return (
    <WizardFlowContent
      flow={flow}
      showNav={isEditing}
      onSubmit={() => void handleSubmit()}
      cancelOnFirstStep={cancelOnFirstStep}
      disabled={isSaving}
      nextIcon={mode === 'create' ? <Plus className="h-4 w-4 mr-1" /> : undefined}
      submitIcon={<Check className="h-4 w-4 mr-1" />}
      submitLabel={isSaving ? busyLabel : idleSubmitLabel}
      navClassName="flex justify-between gap-2"
    >
      {form.saveError ? <p className="text-sm text-destructive">{form.saveError}</p> : null}
      {errors.root ? <p className="text-sm text-destructive">{errors.root}</p> : null}
    </WizardFlowContent>
  )
}
